import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Box } from "@material-ui/core";
import {
  DeletePropertyUnitModelAPI,
  GetAllPropertyUnitModelsAPI,
} from "../../../../../../Services";
import { useSelectedTheme, useTranslate } from "../../../../../../Hooks";
import {
  BasicTable,
  CustomPagination,
  DeleteConfirmationDialog,
  NoDataFound,
  TableEditDeleteActions,
  TableSkeleton,
} from "../../../../../../Components";
import { UnitModelsTableHeaderData } from "./UnitModelsTableHeaderData";
import { useVerticalNav } from "../../../../../../Contexts/VerticalNavContext";
import { PropertyUnitTypeSelector, UnitsModelsManangementSideMenu } from "./UI";

// styles
import useStyle from "./styles";

const UnitModels = forwardRef(({ propretyId }, ref) => {
  const styles = useStyle();
  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [unitsModels, setUnitsModels] = useState({
    result: [],
    totalCount: 0,
  });
  const [activeItem, setActiveItem] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);
  const [isOpenAddUnitModelOpen, setIsOpenAddUnitModelOpen] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: 25,
    pageIndex: 1,
  });
  const [inputValue, setInputValue] = useState(pagination.pageIndex);

  const getAllUnitsModels = useCallback(async () => {
    setIsLoading(true);

    const res = await GetAllPropertyUnitModelsAPI({
      propertyId: propretyId,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });

    if (!(res && res.status && res.status !== 200)) {
      setUnitsModels({
        result: (res && res.result) || [],
        totalCount: (res && res.totalCount) || 0,
      });
    } else {
      setUnitsModels({
        result: [],
        totalCount: 0,
      });
    }

    setIsLoading(false);
  }, [pagination]);

  const reloadData = useCallback(() => {
    getAllUnitsModels();
  });

  const restConfirmationState = () => {
    setActiveItem({});
    setIsOpenDeleteConfirmation(false);
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const res = await DeletePropertyUnitModelAPI(
        activeItem && activeItem.propertyUnitModelId
      );
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("unit-model-delete-successfully"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        reloadData();
      } else
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("unit-model-delete-Failed"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("some-thing-wrong"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } finally {
      setIsLoading(false);
      restConfirmationState();
    }
  };

  const addUnitModelHandler = () => {
    setActiveItem({});
    setIsOpenAddUnitModelOpen(true);
  };

  useImperativeHandle(ref, () => ({
    addUnitModelHandler,
  }));

  useEffect(() => {
    getAllUnitsModels();
  }, [pagination]);

  return (
    <Box className={styles.containerUnitModelsView}>
      <PropertyUnitTypeSelector />
      {isLoading ? (
        <TableSkeleton rowsNum={12} />
      ) : unitsModels?.result?.length > 0 ? (
        <BasicTable
          rowsData={
            unitsModels?.result.map((unit) => {
              return {
                ...unit,
                id: unit.propertyUnitModelId,
              };
            }) || []
          }
          setActiveItem={setActiveItem}
          rowHeight={84}
          columns={UnitModelsTableHeaderData(palette, styles)}
          setActiveItemOnRowClick={(item) => {
            setActiveItem(item);
          }}
          tableActions={({ anchorPosition }) => {
            if (!activeItem || Object.keys(activeItem).length === 0)
              return null;
            return (
              <TableEditDeleteActions
                anchorPosition={anchorPosition}
                onDelete={() => setIsOpenDeleteConfirmation(true)}
                onEdit={() => setIsOpenAddUnitModelOpen(true)}
              />
            );
          }}
        />
      ) : (
        <NoDataFound title={translate("Unit-Models")} />
      )}
      <CustomPagination
        totalItems={unitsModels.totalCount}
        itemsPerPage={pagination.pageSize}
        currentPage={pagination.pageIndex}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onPageChange={(page) =>
          setPagination((prev) => ({ ...prev, pageIndex: page }))
        }
        onItemsPerPageChange={(items) =>
          setPagination((prev) => ({ ...prev, pageSize: items }))
        }
        isLoading={isLoading}
      />
      {Boolean(activeItem) && isOpenDeleteConfirmation && (
        <DeleteConfirmationDialog
          title={translate("Unit-type-will-be-deleted")}
          subTitle={translate(
            "Are-you-sure-you-want-to-delete-this-unit-type-This-action-cannot-be-undone"
          )}
          onClose={() => {
            restConfirmationState();
          }}
          onConfirm={() => {
            handleDelete();
          }}
        />
      )}
      {isOpenAddUnitModelOpen && (
        <UnitsModelsManangementSideMenu
          onOpen={isOpenAddUnitModelOpen}
          onClose={() => {
            setIsOpenAddUnitModelOpen(false);
            setActiveItem({});
          }}
          activeItem={activeItem}
          propretyId={propretyId}
          reloadData={()=>{
            reloadData()
          }}
        />
      )}
    </Box>
  );
});

export default UnitModels;
